<script setup lang="ts">
import {
  Pencil as PencilIcon,
  Plus as PlusIcon,
  Tag as TagIcon,
  Undo2 as Undo2Icon,
} from '@lucide/vue'
import { computed, inject, ref, watch, type Ref } from 'vue'

import ElevationProfileChart from '@/components/map/toolbar/ElevationProfileChart.vue'
import MetricSelector from '@/components/map/toolbar/MetricSelector.vue'
import RouteStatsRow from '@/components/map/toolbar/RouteStatsRow.vue'
import TourDetailPanel from '@/components/map/toolbar/TourDetailPanel.vue'
import WaypointList from '@/components/map/toolbar/WaypointList.vue'
import BaseIconButton from '@/components/ui/BaseIconButton.vue'
import BaseSegmentedControl from '@/components/ui/BaseSegmentedControl.vue'
import BaseSpinner from '@/components/ui/BaseSpinner.vue'
import { useBottomSheet } from '@/composables/useBottomSheet'
import { useEventListener } from '@/composables/useEventListener'
import { hasPermission } from '@/services/permissionService'
import {
  FLOATING_SHEET_BOTTOM_OFFSET,
  mobileSheetFullHeight,
  mobileSheetMaxHeight,
} from '@/services/safeAreaService'
import type { Tour } from '@/types/tour'
import { getCreatorByline } from '@/utils/creatorByline'
import { resolveTourAccentColor } from '@/utils/tourAccentColor'
import {
  getEffectiveTourName,
  getTourGlobalNameSubtitle,
  hasCollectionNameOverride,
} from '@/utils/tourName'
import type {
  TrackProfileAnalyses,
  TrackProfileLocationMatch,
  TrackProfileMetricId,
  TrackProfileVisibleRange,
} from '@/utils/trackProfileAnalysis'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'RouteDetailSheet', inheritAttrs: false })

type RouteSheetTab = 'details' | 'profile' | 'waypoints'
type RouteSheetMode = 'peek' | 'mid' | 'full'
type ProfileBreakdownMetricId = 'surface' | 'wayType'
type ProfileSection = { metricId: ProfileBreakdownMetricId; categoryId: string }

const props = withDefaults(
  defineProps<{
    tour: Tour
    analyses: TrackProfileAnalyses
    loading?: boolean
    isLibraryContext?: boolean
    allowTrackRouteEdit?: boolean
    selectedWaypointIndex?: number | null
    waypointOpenSource?: 'list' | 'map' | null
    activeTab?: RouteSheetTab
    initialMode?: RouteSheetMode
    metric?: TrackProfileMetricId
    cursorDistanceM?: number | null
    visibleRangeM?: TrackProfileVisibleRange | null
    activeMetric?: ProfileBreakdownMetricId | null
    highlightedCategoryId?: string | null
    currentLocation?: TrackProfileLocationMatch | null
    interactionResetKey?: number
    /** Track subset the stats row should reflect (the profiled track, to match the chart). */
    statsTracks?: Tour['tracks']
    /** A→B segment selection metrics; when set, the stats follow the selection. */
    segmentStats?: { distanceM: number; gainM: number; lossM: number } | null
    /** A→B selection range, used to draw the A / B markers on the chart. */
    segmentRange?: TrackProfileVisibleRange | null
    /** Average uphill / downhill grade shown small next to ascent / descent. */
    ascentGradePercent?: number | null
    descentGradePercent?: number | null
    /** Whether the collection exposes a share action. */
    canShareCollection?: boolean
  }>(),
  {
    loading: false,
    isLibraryContext: false,
    allowTrackRouteEdit: true,
    selectedWaypointIndex: null,
    waypointOpenSource: null,
    activeTab: 'details',
    initialMode: 'mid',
    metric: 'gradient',
    cursorDistanceM: null,
    visibleRangeM: null,
    activeMetric: null,
    highlightedCategoryId: null,
    currentLocation: null,
    statsTracks: undefined,
    segmentStats: null,
    segmentRange: null,
    ascentGradePercent: null,
    descentGradePercent: null,
    canShareCollection: false,
  },
)

const emit = defineEmits<{
  'update:activeTab': [tab: RouteSheetTab]
  'update:metric': [metric: TrackProfileMetricId]
  'update:cursorDistanceM': [value: number | null]
  'update:visibleRangeM': [value: TrackProfileVisibleRange | null]
  'select:range': [value: TrackProfileVisibleRange]
  'hover:section': [value: ProfileSection | null]
  'cycle:section': [value: ProfileSection]
  'leave:profile': []
  'zoom-tour': [tourId: string]
  'zoom-track': [tourId: string, trackIndex: number]
  'zoom-waypoint': [tourId: string, waypointIndex: number]
  'zoom-route-point': [tourId: string, routePointIndex: number]
  back: []
  'open-waypoint-detail': [tourId: string, waypointIndex: number, source?: 'list' | 'map']
  'close-waypoint-detail': []
  'delete-waypoint': [tourId: string, waypointIndex: number]
  'edit-waypoint': [tourId: string, waypointIndex: number]
  'enter-track-edit': [tourId: string, trackClientId: string]
  'retain-unpinned-tour-detail': [tour: Tour]
  'clear-segment': []
  'share-collection': []
  'request-rename': []
  'reset-name': []
  /** Current settled sheet height (px) so the map can pad its fit above the sheet. */
  'update:height': [height: number]
  /** Current settled snap mode, so the map host can restore the user's last route-sheet height. */
  'update:mode': [mode: RouteSheetMode]
}>()

const TABS: Array<{ id: RouteSheetTab; label: string }> = [
  { id: 'details', label: 'Details' },
  { id: 'profile', label: 'Profile' },
  { id: 'waypoints', label: 'Waypoints' },
]

const activeTab = ref<RouteSheetTab>(props.activeTab)
const bodyRef = ref<HTMLElement | null>(null)
watch(
  () => props.activeTab,
  (tab) => {
    if (tab !== activeTab.value) activeTab.value = tab
  },
)
const selectTab = (tab: RouteSheetTab) => {
  if (tab === activeTab.value) return
  activeTab.value = tab
  emit('update:activeTab', tab)
  // Each tab body shares one scroll container — reset so a new tab starts at the top.
  if (bodyRef.value) bodyRef.value.scrollTop = 0
  // Opening a tab body should expand the sheet beyond peek.
  if (sheet.mode.value === 'peek') sheet.snapTo('mid')
}
// Options for BaseSegmentedControl (badge = waypoint count; roving keyboard lives in the control).
const tabOptions = computed(() =>
  TABS.map((tab) => ({
    value: tab.id,
    label: tab.label,
    badge: tabBadge(tab.id),
    testId: `route-detail-tab-${tab.id}`,
  })),
)

const prefersReducedMotion =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false

const metricModel = computed({
  get: () => props.metric,
  set: (value: TrackProfileMetricId) => emit('update:metric', value),
})

const viewportHeight = ref(typeof window === 'undefined' ? 844 : window.innerHeight)
// Medium snap is tuned to the elevation-profile height (Komoot-style): persistent header
// (handle + stats + tabs ≈ 130px) + the Profile tab's metric selector + tall chart (≈ 200px) — so
// mid shows the chart and stops just before the surface/way-type breakdowns, keeping the map as the
// premium surface. It's a roughly fixed pixel height (the chart is fixed px, not a viewport %), only
// clamped so it always sits between peek and full on short screens. Full reveals the breakdowns.
const ELEVATION_PROFILE_MID_PX = 330
const sheetHeights = computed<Record<'peek' | 'mid' | 'full', number>>(() => {
  const height = viewportHeight.value
  // Peek is a glanceable summary — handle + stat row (plus the A→B caption when a segment is
  // selected). The track name lives in the floating top-centre title and the identity/tabs live in
  // the expanded view, so the peek matches the collection drawer's peek (handle + stats).
  const full = mobileSheetFullHeight(height)
  return {
    peek: props.segmentStats ? 116 : 80,
    mid: Math.min(ELEVATION_PROFILE_MID_PX, full - 40),
    full,
  }
})
const sheet = useBottomSheet({
  modes: ['peek', 'mid', 'full'] as const,
  heights: sheetHeights,
  initialMode: props.initialMode,
})

// Float above the tab bar with the shared 8px gap (self-contained rounded card), consistent with
// the list drawer and the other floating sheets.
// Reactive bottom offset from the map container: collapses the tab bar's 64px once the tab bar is
// hidden at a detail level (the route sheet is always at a detail level), reclaiming that space.
const floatingSheetBottom = inject<Ref<string>>(
  'floatingSheetBottomOffset',
  ref(FLOATING_SHEET_BOTTOM_OFFSET),
)

const sheetStyle = computed(() => ({
  height: `${sheet.height.value}px`,
  maxHeight: mobileSheetMaxHeight(floatingSheetBottom.value),
  bottom: floatingSheetBottom.value,
  zIndex: String(Z_INDEX.MOBILE_TOOLBAR),
  transition:
    sheet.isDragging.value || prefersReducedMotion
      ? 'none'
      : 'height 180ms cubic-bezier(0.25, 0.46, 0.45, 0.94)',
}))

useEventListener(window, 'resize', () => {
  viewportHeight.value = window.innerHeight
})

// Publish the settled snap height so the map can pad its fit to the visible area above the
// sheet (peek/mid/full), keeping a selected route centred in the remaining map space.
const settledHeight = computed(() => sheetHeights.value[sheet.mode.value])
watch(settledHeight, (height) => emit('update:height', height), { immediate: true })
watch(sheet.mode, (mode) => emit('update:mode', mode), { immediate: true })

const showChart = computed(
  () => props.analyses.gradient.hasElevation && props.analyses.gradient.points.length > 1,
)
const showBody = computed(() => sheet.mode.value !== 'peek')
const tourColor = computed(() => resolveTourAccentColor(props.tour))
const effectiveTourName = computed(() => getEffectiveTourName(props.tour))
const tourCreatorByline = computed(() =>
  props.isLibraryContext ? null : getCreatorByline(props.tour),
)
const tourNameSubtitle = computed(() => getTourGlobalNameSubtitle(props.tour))
const hasCollectionNameReset = computed(() => hasCollectionNameOverride(props.tour))
// Visibility only — creator-only edit is enforced (with an explanatory dialog) by the store
// action itself, so the affordance stays visible to every collaborator.
const canRename = computed(() => hasPermission('edit_track'))
const startRename = () => {
  if (!canRename.value) return
  emit('request-rename')
}
const resetCollectionName = () => {
  if (!hasCollectionNameReset.value) return
  emit('reset-name')
}

const waypointCount = computed(() => props.tour.waypoints.length)
const tabBadge = (tab: RouteSheetTab): number | null =>
  tab === 'waypoints' ? waypointCount.value : null

const canEditWaypoints = computed(() => hasPermission('edit_waypoint'))
const canDeleteWaypoints = computed(() => hasPermission('delete_waypoint'))
</script>

<template>
  <div
    class="pointer-events-auto fixed left-3 right-3 flex flex-col overflow-hidden rounded-2xl border border-gray-200/70 bg-white/95 shadow-[0_-8px_30px_rgba(15,23,42,0.14)] backdrop-blur-xl"
    :style="sheetStyle"
    data-testid="route-detail-sheet"
  >
    <!-- Persistent header (drag area): identity · primary stats · A→B selection · tabs.
         This doubles as the peek state, so it carries the route's at-a-glance information. -->
    <div
      class="shrink-0 touch-none px-4 pt-2 pb-1"
      :class="sheet.isDragging.value ? 'cursor-grabbing' : 'cursor-grab'"
      data-testid="route-detail-sheet-handle"
      @pointerdown="sheet.onPointerDown"
      @pointermove="sheet.onPointerMove"
      @pointerup="sheet.onPointerUp"
      @pointercancel="sheet.onPointerUp"
    >
      <div class="mb-1.5 flex justify-center">
        <div class="h-1.5 w-10 rounded-full bg-neutral-300"></div>
      </div>

      <!-- Primary stats (single row, always visible — this is the peek summary). Distance · Ascent ·
           Descent · Time with up/down grades. The track's name + colour dot live in the floating
           top-centre title (not repeated here), and rename/share move into the Details tab, so the
           header stays a compact "handle + stats" strip that gives the map back its vertical space. -->
      <RouteStatsRow
        :tour="tour"
        :tracks="statsTracks"
        :override="segmentStats"
        :ascent-grade-percent="ascentGradePercent"
        :descent-grade-percent="descentGradePercent"
        class="mt-1"
      />

      <!-- A→B selection caption with a Clear affordance -->
      <div
        v-if="segmentStats"
        class="mt-2 flex items-center justify-between rounded-lg bg-emerald-50 px-2.5 py-1 ring-1 ring-emerald-600/15"
        data-testid="route-detail-segment-caption"
      >
        <span class="text-xs font-semibold text-emerald-700">A → B selection</span>
        <button
          type="button"
          class="-my-1 rounded px-2 py-2 text-xs font-semibold text-emerald-700 transition-colors hover:bg-emerald-100 hover:cursor-pointer"
          @pointerdown.stop
          @click.stop="emit('clear-segment')"
        >
          Clear
        </button>
      </div>

      <!-- Segmented tabs — only in the expanded view; the peek stays a glanceable stats summary
           (back + name + stat row, plus the A→B caption when a segment is selected). -->
      <BaseSegmentedControl
        v-show="showBody"
        class="mt-2"
        variant="tabs"
        :model-value="activeTab"
        :options="tabOptions"
        aria-label="Route detail tabs"
        option-id-prefix="route-tab"
        panel-id-prefix="route-panel"
        @update:model-value="selectTab($event as RouteSheetTab)"
      />
    </div>

    <!-- Body -->
    <div ref="bodyRef" v-show="showBody" class="min-h-0 flex-1 overflow-y-auto overscroll-contain">
      <!-- Details -->
      <div
        v-show="activeTab === 'details'"
        id="route-panel-details"
        role="tabpanel"
        aria-labelledby="route-tab-details"
        class="flex min-h-0 flex-col"
      >
        <!-- Name + collection share. Selecting an editable name opens the shared rename sheet; this
             row stays outside the persistent handle/stats header. -->
        <div class="flex items-start gap-2 px-4 pb-1 pt-3">
          <span
            class="mt-[5px] h-2.5 w-2.5 shrink-0 rounded-full"
            :style="{ backgroundColor: tourColor }"
          ></span>
          <div class="min-w-0 flex-1">
            <div class="flex items-center gap-1 min-w-0">
              <button
                v-if="canRename"
                type="button"
                class="truncate rounded text-left text-base font-semibold text-neutral-900 hover:cursor-pointer hover:ring-1 hover:ring-neutral-300"
                :title="effectiveTourName"
                data-testid="route-detail-name"
                @click.stop="startRename"
              >
                {{ effectiveTourName }}
              </button>
              <span
                v-else
                class="truncate text-base font-semibold text-neutral-900"
                :title="effectiveTourName"
                data-testid="route-detail-name"
                >{{ effectiveTourName }}</span
              >
              <span
                v-if="tourCreatorByline"
                class="shrink-0 text-xs font-normal text-neutral-500"
                data-testid="route-detail-creator-byline"
              >
                {{ tourCreatorByline }}
              </span>
              <span
                v-if="hasCollectionNameOverride(props.tour)"
                class="shrink-0 text-gray-400"
                :title="tourNameSubtitle ?? undefined"
                aria-label="Custom name"
              >
                <TagIcon class="h-4 w-4" />
              </span>
              <BaseIconButton
                v-if="canRename"
                size="sm"
                class="shrink-0"
                title="Rename"
                aria-label="Rename tour"
                data-testid="route-detail-rename"
                @click.stop="startRename"
              >
                <PencilIcon class="h-3.5 w-3.5" />
              </BaseIconButton>
            </div>
            <div
              v-if="tourNameSubtitle || hasCollectionNameReset"
              class="mt-0.5 flex items-center gap-1.5"
            >
              <p
                v-if="tourNameSubtitle"
                class="truncate text-xs text-neutral-500"
                :title="tourNameSubtitle"
                data-testid="route-detail-name-subtitle"
              >
                {{ tourNameSubtitle }}
              </p>
              <button
                v-if="hasCollectionNameReset"
                type="button"
                class="shrink-0 rounded-full p-1 text-gray-500 transition-colors hover:bg-black/5 hover:text-gray-800 hover:cursor-pointer"
                title="Reset name"
                aria-label="Reset name"
                data-testid="route-detail-name-reset"
                @pointerdown.stop
                @click.stop="resetCollectionName"
              >
                <Undo2Icon class="h-3 w-3" />
              </button>
            </div>
          </div>
        </div>

        <TourDetailPanel
          embedded
          :tour="tour"
          :loading="loading"
          :allow-track-route-edit="allowTrackRouteEdit"
          :selected-waypoint-index="selectedWaypointIndex"
          :waypoint-open-source="waypointOpenSource"
          :is-library-context="isLibraryContext"
          class="min-h-0 flex-1"
          @back="emit('back')"
          @zoom-tour="emit('zoom-tour', $event)"
          @zoom-track="(tourId, trackIndex) => emit('zoom-track', tourId, trackIndex)"
          @zoom-waypoint="(tourId, waypointIndex) => emit('zoom-waypoint', tourId, waypointIndex)"
          @open-waypoint-detail="
            (tourId, waypointIndex, source) =>
              emit('open-waypoint-detail', tourId, waypointIndex, source)
          "
          @close-waypoint-detail="emit('close-waypoint-detail')"
          @delete-waypoint="
            (tourId, waypointIndex) => emit('delete-waypoint', tourId, waypointIndex)
          "
          @enter-track-edit="
            (tourId, trackClientId) => emit('enter-track-edit', tourId, trackClientId)
          "
          @retain-unpinned-tour-detail="emit('retain-unpinned-tour-detail', $event)"
        >
          <template #stats>
            <!-- Headline stats live in the persistent header; here we only add the
                 activity-only time / avg speed (empty for routes). -->
            <RouteStatsRow :tour="tour" :tracks="statsTracks" variant="secondary" />
          </template>
        </TourDetailPanel>
      </div>

      <!-- Profile (headline stats are in the persistent header) -->
      <div
        v-show="activeTab === 'profile'"
        id="route-panel-profile"
        role="tabpanel"
        aria-labelledby="route-tab-profile"
        class="space-y-2 px-4 py-2"
      >
        <template v-if="showChart">
          <div class="-mb-1">
            <MetricSelector v-model="metricModel" align="left" />
          </div>
          <ElevationProfileChart
            tall
            :analyses="analyses"
            :metric="metric"
            :segment-range="segmentRange"
            :cursor-distance-m="cursorDistanceM"
            :visible-range-m="visibleRangeM"
            :active-metric="activeMetric"
            :highlighted-category-id="highlightedCategoryId"
            :current-location="currentLocation"
            :interaction-reset-key="interactionResetKey"
            @update:cursor-distance-m="emit('update:cursorDistanceM', $event)"
            @update:visible-range-m="emit('update:visibleRangeM', $event)"
            @select:range="emit('select:range', $event)"
            @hover:section="emit('hover:section', $event)"
            @cycle:section="emit('cycle:section', $event)"
            @leave:profile="emit('leave:profile')"
          />
        </template>
        <div
          v-else-if="loading"
          role="status"
          class="flex flex-col items-center gap-2 py-8 text-center text-sm text-neutral-500"
        >
          <BaseSpinner size="md" />
          <span>Loading elevation…</span>
        </div>
        <div
          v-else
          role="status"
          class="flex flex-col items-center gap-1 py-8 text-center text-sm text-neutral-500"
        >
          <span>No elevation data for this route</span>
        </div>
      </div>

      <!-- Waypoints -->
      <div
        v-show="activeTab === 'waypoints'"
        id="route-panel-waypoints"
        role="tabpanel"
        aria-labelledby="route-tab-waypoints"
        class="px-2 py-2"
      >
        <WaypointList
          :tour="tour"
          :show-header="false"
          :expanded="true"
          :can-edit-waypoints="canEditWaypoints"
          :can-delete-waypoints="canDeleteWaypoints"
          @zoom-waypoint="(tourId, waypointIndex) => emit('zoom-waypoint', tourId, waypointIndex)"
          @open-waypoint-detail="
            (tourId, waypointIndex, source) =>
              emit('open-waypoint-detail', tourId, waypointIndex, source)
          "
        />
        <div
          v-if="waypointCount === 0"
          role="status"
          class="flex flex-col items-center gap-1 py-8 text-center text-sm text-neutral-500"
        >
          <PlusIcon class="h-5 w-5" />
          <span>No waypoints yet</span>
        </div>
      </div>
    </div>
  </div>
</template>
